const DEFAULT_OPTIONS = {
    cellPadding: 26,
    generalFloor: 112,
    readableFloorMax: 180,
    bodyFont: '14px "Microsoft YaHei", sans-serif',
    headerFont: '600 14px "Microsoft YaHei", sans-serif',
}

function clamp(value, min, max) {
    return Math.min(max, Math.max(min, value))
}

// Match white-space: normal. NBSP remains significant in the rendered content.
function collapseWhitespace(value) {
    return String(value == null ? '' : value).replace(/[\t\n\r\f ]+/g, ' ').replace(/^ | $/g, '')
}

function getVisibleLines(row, column) {
    const value = row && row[column.key]
    const source = Array.isArray(value) ? value : [value]
    const lines = []
    source.forEach(item => {
        const value = item && typeof item === 'object'
            ? (column.type === 'link' ? (item.key || item.value) : item.value)
            : item
        // The component already creates separate text entries for explicit
        // newlines. Whitespace inside any one entry is collapsed by its CSS.
        lines.push(collapseWhitespace(value))
    })
    return lines
}

export function measureColumns(columns, rows, options = {}) {
    const settings = { ...DEFAULT_OPTIONS, ...options }
    let context = null
    try {
        context = document.createElement('canvas').getContext('2d')
    } catch (error) {
        // Canvas can be disabled. Retain useful, bounded layout in that case.
    }
    const cache = new Map()
    const measure = (text, font) => {
        const key = `${font}\n${text}`
        if (cache.has(key)) return cache.get(key)
        let width = 0
        if (context) {
            context.font = font
            width = context.measureText(text).width
        } else {
            // Conservative fallback; a later font-ready measurement can refine it.
            for (const character of text) width += character.charCodeAt(0) > 255 ? 14 : 8
        }
        if (cache.size < 4096) cache.set(key, width)
        return width
    }

    return columns.map(column => {
        const header = collapseWhitespace(column.title)
        const headerWidth = measure(header, settings.headerFont)
        let contentWidth = 0
        let wordWidth = 0
        rows.forEach(row => {
            getVisibleLines(row, column).forEach(line => {
                contentWidth = Math.max(contentWidth, measure(line, settings.bodyFont))
                // A running maximum avoids argument-stack overflows on huge cells.
                line.split(/[\s\-/_.?&=#:]+/).forEach(word => {
                    if (word) wordWidth = Math.max(wordWidth, measure(word, settings.bodyFont))
                })
            })
        })

        const natural = Math.max(settings.generalFloor,
            Math.max(headerWidth, contentWidth) + settings.cellPadding)
        const readable = Math.max(headerWidth, wordWidth) + settings.cellPadding
        // Short labels should stay on one line even when long neighbours shrink.
        const floor = natural <= settings.readableFloorMax ? natural
            : clamp(readable, settings.generalFloor, settings.readableFloorMax)
        return {
            type: column.type,
            floor,
            natural,
            preferred: natural,
            contentMax: natural,
        }
    })
}

function getSoftTableWidth(columnCount, availableWidth) {
    // The design's table is about 1235px on a 1920px canvas; the available
    // content area is about 1652px. Keep that proportion for sparse tables
    // instead of shrinking one/two/three columns to fixed pixel widths.
    return columnCount <= 3 ? availableWidth * 0.75 : availableWidth
}

// All useful content is accommodated before this decorative expansion.
// Square-root weights retain visual hierarchy without allowing long columns to
// absorb all spare space. Identical short columns still expand equally.
function fillRemainingSpace(widths, targetWidth, metrics, linkShare) {
    const limits = metrics.map((metric, index) => metric.type === 'link'
        ? Math.max(widths[index], targetWidth * linkShare) : targetWidth)
    const weights = widths.map(width => Math.sqrt(width))
    let remaining = targetWidth - widths.reduce((sum, width) => sum + width, 0)
    let active = widths.map((_, index) => index).filter(index => widths[index] < limits[index])
    while (remaining > 0.0001 && active.length) {
        const weightTotal = active.reduce((sum, index) => sum + weights[index], 0)
        let used = 0
        active.forEach(index => {
            const addition = Math.min(limits[index] - widths[index], remaining * weights[index] / weightTotal)
            widths[index] += addition
            used += addition
        })
        remaining -= used
        active = active.filter(index => limits[index] - widths[index] > 0.0001)
    }
    return widths
}

// Under pressure, complete text lines are more useful than unbroken URLs.
// Transfer only the links' spare width, retaining up to an average column
// (capped at 320px) for readable links. Protection fades for lines longer than
// 480px and ends at 640px: long paragraphs should wrap alongside long URLs.
function prioritizeTextLines(widths, naturalWidths, floors, metrics, targetWidth) {
    const linkReadingWidth = Math.min(320, targetWidth / metrics.length)
    const deficits = metrics.map((metric, index) => metric.type === 'text'
        ? Math.max(0, Math.min(naturalWidths[index], 480) - widths[index])
            * clamp((640 - naturalWidths[index]) / 160, 0, 1) : 0)
    const spare = metrics.map((metric, index) => {
        if (metric.type !== 'link') return 0
        const reserve = Math.min(naturalWidths[index], Math.max(floors[index], linkReadingWidth))
        return Math.max(0, widths[index] - reserve)
    })
    const demand = deficits.reduce((sum, width) => sum + width, 0)
    const supply = spare.reduce((sum, width) => sum + width, 0)
    if (!demand || !supply) return widths

    const transfer = Math.min(demand, supply)
    return widths.map((width, index) => width
        + transfer * deficits[index] / demand
        - transfer * spare[index] / supply)
}

export function allocateColumnWidths(metrics, availableWidth) {
    if (!metrics.length || !Number.isFinite(availableWidth) || availableWidth <= 0) {
        return { tableWidth: 0, columnWidths: [] }
    }

    const naturalWidths = metrics.map(metric => Math.max(1,
        metric.natural || metric.contentMax || metric.preferred || metric.floor || 1))
    const naturalTotal = naturalWidths.reduce((sum, width) => sum + width, 0)
    const targetWidth = Math.min(availableWidth,
        Math.max(naturalTotal, getSoftTableWidth(metrics.length, availableWidth)))
    // Preserve modest single-line labels before shrinking paragraphs. The
    // budget follows the container/column count, so seven columns on a small
    // desktop do not acquire an impossible collection of fixed minimums.
    const singleLineBudget = Math.min(280, targetWidth / metrics.length)
    const floors = metrics.map((metric, index) => naturalWidths[index] <= singleLineBudget
        ? naturalWidths[index]
        : Math.min(naturalWidths[index], Math.max(1, metric.floor || 1)))
    const floorTotal = floors.reduce((sum, width) => sum + width, 0)
    // Limits apply to real link demand, never to the final full-width fill.
    // A short label uses its label width, irrespective of its underlying URL.
    const linkShare = metrics.length === 1 ? 1 : metrics.length === 2 ? 0.75 : 0.6
    const desired = naturalWidths.map((width, index) => metrics[index].type === 'link'
        ? Math.max(floors[index], Math.min(width, targetWidth * linkShare))
        : Math.min(width, targetWidth))
    const desiredTotal = desired.reduce((sum, width) => sum + width, 0)
    let widths

    if (desiredTotal <= targetWidth) {
        widths = fillRemainingSpace(desired.slice(), targetWidth, metrics, linkShare)
    } else if (floorTotal <= targetWidth) {
        // When paragraphs compete with links, first aim for readable wrapping
        // widths. Extra sentences must not keep taking space from other columns.
        // This is a soft goal: spare room can still expand a long text column.
        const hasFlexibleLink = metrics.some((metric, index) => metric.type === 'link'
            && desired[index] > floors[index])
        const readingTargets = desired.map((width, index) => hasFlexibleLink && metrics[index].type === 'text'
            ? Math.max(floors[index], Math.min(width, 480)) : width)
        const readingTotal = readingTargets.reduce((sum, width) => sum + width, 0)
        if (readingTotal <= targetWidth) {
            const ratio = (targetWidth - readingTotal) / (desiredTotal - readingTotal)
            widths = readingTargets.map((width, index) => width + (desired[index] - width) * ratio)
        } else {
            const ratio = (targetWidth - floorTotal) / (readingTotal - floorTotal)
            widths = readingTargets.map((width, index) => floors[index] + (width - floors[index]) * ratio)
        }
        widths = prioritizeTextLines(widths, naturalWidths, floors, metrics, targetWidth)
    } else {
        // Very narrow containers / many long headings: wrap rather than overflow.
        widths = floors.map(width => width * targetWidth / floorTotal)
    }

    return { tableWidth: targetWidth, columnWidths: widths }
}
